<template>
    <div :class="[
        'shrink-0 sticky top-0 z-50 bg-white w-full h-[64px] border-b border-gray-DFDFDF',
        'px-[20px] lg:px-[40px] flex items-center justify-between font-oceanside',
    ]">
        <reset-panel-button
            class="w-[18px]"
        />
        <h1
            class="flex-1 text-center text-base -mr-[19px] pointer-events-none flex flex-col font-semibold"
        >
            {{ $t('CATEGORY_STAMPS') }}
        </h1>
        <close-button
            width="18.84"
            @click="handleClose"
        />
    </div>

    <placement-header-alt
        v-if="!isMobile"
        :placement="visiblePlacementsStamps?.[0]"
        :placementData="placementData"
        :hide-combine="true"
    />

    <mobile-product-renderer v-if="isMobile" />

    <div class="sticky top-0 z-10 -mt-[1px]">
        <step-options-color-controls
            v-if="isAnyPaintfillSelected"
            :is-active="true"
        />
    </div>

    <div class="flex-1 min-h-0 overflow-y-auto pb-[20px] font-oceanside px-[20px] lg:px-[40px] relative pt-[20px]">
        <step-options-initials-buttons
            :main-placement="visiblePlacementsStamps?.[0]"
            :option="stampOption"
            :has-add-remove-button="true"
            :remove-selected-on-add-remove-click="true"
            :has-image="!isMobile"
            :navigate-on-click="false"
            @unselect="handleRemoveSelection"
            background-size="auto 100%"
        />
        <div
            v-for="(placement, index) in visiblePlacementsStampsNonColors"
            :key="placement.code"
        >
            <placement-header
                :placement="{
                    ...placement,
                    name: 'Icons',
                    selectedValue: selectedValue(placement),
                }"
                :index="index"
            ></placement-header>

            <div class="flex gap-x-[12px] mb-[24px]">
                <template
                    v-for="option in iconOptions(placement)"
                    :key="option.code"
                >
                    <button
                        v-if="!option.value.includes('NoThanks')"
                        @click="selectActual({ placement, option })"
                        :class="[
                            'w-[48px] h-[48px] bg-gray-F6F6F6 border cursor-pointer bg-no-repeat',
                            'hover:border-black bg-top bg-cover',
                            { 'border-black': selectedValue(placement) === option.value },
                            { 'border-gray-DFDFDF hover:border-black': selectedValue(placement) !== option.value },
                            'focus:outline-none focus:ring-1 focus:ring-black focus:border-black',
                        ]"
                        :style="{
                            backgroundImage: `url(&quot;${getImage(option)}&quot;)`,
                            backgroundSize: 'auto 100%',
                        }"
                    />
                </template>
            </div>
        </div>

        <div v-if="allowColors && visiblePlacementsStampsColors.length > 0">
            <step-options-front-stamps-list
                :has-error-state="hasInvalidStampColor"
                @color-selected="handleStampColorSelected"
            />
        </div>
    </div>

    <step-options-apply-selections
        class="shrink-0"
        @apply-selections="handleClose"
        @handle-disabled="handleDisabled"
        :is-disabled="isDisabled"
        :should-show-stamps-requirement="shouldShowStampsRequirement"
        :is-dirty="effectiveIsDirty"
        :should-close-group-overlay="false"
        :button-text="$t('APPLY_STAMPS')"
    />
</template>

<script>
    import allStores from '../stores/all';
    import CloseButton from './buttons/CloseButton.vue';
    import PlacementHeader from './PlacementHeader.vue';
    import ResetPanelButton from './buttons/ResetPanelButton.vue';
    import StepOptionsApplySelections from './StepOptionsApplySelections.vue';
    import StepOptionsFrontStampsList from './StepOptionsFrontStampsList.vue';
    import MobileProductRenderer from './MobileProductRenderer.vue';
    import StepOptionsInitialsButtons from './buttons/StepOptionsInitialsButtons.vue';
    import PlacementHeaderAlt from './PlacementHeaderAlt.vue';
    import StepOptionsColorControls from './StepOptionsColorControls.vue';
    import getImage from '../helpers/getimage';

    export default {
        name: 'StepOptionsFrontStampsOptions',

        mixins: [allStores],

        components: {
            CloseButton,
            PlacementHeader,
            ResetPanelButton,
            StepOptionsApplySelections,
            StepOptionsFrontStampsList,
            MobileProductRenderer,
            StepOptionsInitialsButtons,
            PlacementHeaderAlt,
            StepOptionsColorControls,
        },

        props: {
            visiblePlacementsStamps: {
                type: Array,
                required: true,
            },

            isDirty: {
                type: Boolean,
                default: false,
            },
        },

        computed: {
            placementData() {
                const stampsGroup = this.customizerStore.visibleGroups
                    ?.find((g) => g.name === 'Stamps');

                return {
                    placements: [
                        ...((stampsGroup?.placements) || []),
                    ],
                };
            },

            isMobile() {
                return this.appStore.isMobile;
            },

            isAnyPaintfillSelected() {
                return this.customizerStore.isAnyPaintfillSelected();
            },

            enableFocusLock() {
                return this.appStore.activeNestedPanel?.panelId === 'frontStampsOptions';
            },

            effectiveIsDirty() {
                return this.isDirty || this.hasAttemptedApply;
            },

            hasAttemptedApply() {
                return this.customizerStore.hasFrontNestedPanelValidationAttempted('stamps');
            },

            viewController() {
                return this.customizerStore.viewController;
            },

            visiblePlacementsStampsNonColors() {
                if (!Array.isArray(this.visiblePlacementsStamps)) { return []; }

                return this.visiblePlacementsStamps
                    .filter((placement) => !placement.code.includes('Color'));
            },

            visiblePlacementsStampsColors() {
                if (!Array.isArray(this.visiblePlacementsStamps)) { return []; }

                return this.visiblePlacementsStamps
                    .filter((placement) => placement.code.includes('Color'));
            },

            allowColors() {
                return this.visiblePlacementsStamps
                    .some((placement) => placement.custom?.['v-allow-colors'] === 'yes');
            },

            stampColorErrorStateKey() {
                return 'Front-StampColor';
            },

            hasStamps() {
                const stamps = this.viewController?.selectedAtPlacement('Stamp');

                return !!(stamps && !stamps.placeholder && stamps.code !== 'Stamp-Stamp-Blank');
            },

            hasSelectedStampColor() {
                const frontStampsOption = this.customizerStore.uiStepSubOptions?.frontStamps
                    ?.find((option) => option.name === 'StampColor');

                if (frontStampsOption?.selectedColor) {
                    return true;
                }

                const selectedColor = this.viewController.selectedAtPlacement('StampColor');

                return !!(selectedColor && !selectedColor.placeholder);
            },

            hasInvalidStampColor() {
                return this.customizerStore.panelsInErrorState.includes(this.stampColorErrorStateKey);
            },

            hasStampsColor() {
                return this.hasSelectedStampColor;
            },

            shouldShowStampsRequirement() {
                return this.hasStamps && this.allowColors && !this.hasStampsColor;
            },

            stampOption() {
                const stampPlacement = this.visiblePlacementsStamps.find((placement) => placement.code === 'Stamp');

                const component = stampPlacement?.components?.find((option) => !option.placeholder);

                if (!component) {
                    return null;
                }

                return {
                    selected: this.hasStamps,
                    label: 'Stamps',
                    value: 'Stamps',
                    raw: {
                        ...component,
                        custom: {
                            ...(component.custom || {}),
                            ...{
                                'v-thumbnail-asset': 'PutterFace-StampsSelected',
                            },
                        },
                    },
                };
            },

            isComplete() {
                if (!this.hasStamps) {
                    return false;
                }

                if (!this.allowColors) {
                    return true;
                }

                return this.hasStampsColor;
            },

            isDisabled() {
                return !this.isComplete;
            },
        },

        watch: {
            isComplete(value) {
                if (value) {
                    this.customizerStore.clearFrontNestedPanelValidationAttempted('stamps');
                    this.clearStampColorError();
                }
            },

            'customizerStore.uiStepSubOptions.frontStamps': {
                deep: true,
                handler() {
                    if (this.effectiveIsDirty) {
                        this.updateStampColorError();
                    }
                },
            },
        },

        methods: {
            updateStampColorError() {
                if (!this.hasStamps || !this.allowColors || this.hasSelectedStampColor) {
                    this.clearStampColorError();
                    return;
                }

                this.customizerStore.addPanelToErrorState(this.stampColorErrorStateKey);
            },

            clearStampColorError() {
                this.customizerStore.removePanelFromErrorState(this.stampColorErrorStateKey);
            },

            handleStampColorSelected() {
                if (this.hasSelectedStampColor) {
                    this.clearStampColorError();
                }
            },

            handleRemoveSelection() {
                this.customizerStore.resetStamps();
                this.clearStampColorError();
                this.appStore.closeNestedPanel();
                this.customizerStore.setActiveView('Face');
            },

            iconOptions(placement) {
                const options = this.customizerStore.sortOptions(placement, this.selectedValue(placement));
                const list = [];

                // eslint-disable-next-line no-restricted-syntax
                for (const option of options) {
                    if (this.customizerStore.isStampAllowed(option.raw)) {
                        list.push(option);
                    }
                }

                return list;
            },

            selectActual({ placement, option }) {
                this.customizerStore.setSelection(placement.code, option.value);
                this.customizerStore.setFrontSectionActive('stamps', true);
                this.customizerStore.setFrontNoCustomizationSelected('Face/Sole', false);
                this.customizerStore.setActiveView('Face');
            },

            selectedValue(placement) {
                return this.viewController?.selectedAtPlacement(placement.code)?.code || null;
            },

            getImage(option) {
                return getImage(this.viewController, option, { includeProductSuffix: false });
            },

            handleDisabled() {
                this.customizerStore.setFrontNestedPanelValidationAttempted('stamps', true);
                this.updateStampColorError();
            },

            handleClose() {
                this.appStore.closeNestedPanel();
                this.customizerStore.setActiveView('Face');
            },
        },
    };
</script>
